import { API_URL } from '../config';

// Only a validated response from the authenticated, owned endpoint is evidence.
export function parseDeletion(body) {
  const operation = body?.deletion;
  if (!operation || typeof operation.operation_id !== 'string' || !operation.operation_id.trim()
    || !['identity_pending', 'data_cleanup_pending', 'completed'].includes(operation.phase)
    || !Number.isSafeInteger(operation.requested_at) || operation.requested_at < 0
    || (operation.phase === 'completed'
      ? !Number.isSafeInteger(operation.completed_at) || operation.completed_at < operation.requested_at
      : operation.completed_at !== null)) throw new Error('Unrecognized deletion status');
  return operation;
}

// A stalled transport/body must release the UI into a checkable uncertainty.
// Aborting browser waiting never cancels a durable server operation.
export async function deletionRequest(url, options, consume) {
  const controller = new AbortController();
  const external = options.signal;
  if (external?.aborted) throw new Error('Deletion request interrupted');
  let timer;
  let abort;
  const stopped = new Promise((_, reject) => {
    abort = () => { controller.abort(); reject(new Error('Deletion request interrupted')); };
    external?.addEventListener('abort', abort, {once:true});
    timer = setTimeout(abort, 15000);
    if (external?.aborted) abort();
  });
  try {
    return await Promise.race([fetch(url, {...options,signal:controller.signal}).then(consume), stopped]);
  } finally { clearTimeout(timer); external?.removeEventListener('abort', abort); }
}

export async function readDeletion(token, signal) {
  return deletionRequest(`${API_URL}/profile/deletion`, {
    headers: { Authorization: `Bearer ${token}` }, signal,
  }, async response => {
    const body = await response.json();
    if (response.status === 404 && body?.error === 'deletion_not_requested') return null;
    if (response.status !== 200) throw new Error('Deletion status unavailable');
    return parseDeletion(body);
  });
}

export function isDeletionGuard(response, body) {
  return response.status === 409 && body?.error === 'account_deletion_requested';
}

export async function checkDeletionGuard(response, reportDeletion, signal) {
  if (response.status !== 409 || signal?.aborted) return;
  const body = await (response.clone ? response.clone() : response).json();
  if (!signal?.aborted && isDeletionGuard(response, body)) {
    reportDeletion?.();
    throw new Error('Account deletion requested');
  }
}
